import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Workflow } from 'lucide-react';
import {
  api,
  type DashboardAiSummary,
  type DashboardCardType,
  type DashboardExtras,
  type DashboardGcal,
  type DashboardExternalSync,
  type DashboardLastNote,
  type DashboardPipeline,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PipelineKpiCard, type KpiMetric } from '@/components/PipelineKpiCard';
import { CardGridSkeleton } from '@/components/Skeletons';

function metricsFor(p: DashboardPipeline): KpiMetric[] {
  return [
    { label: '24h events', value: p.events_24h },
    {
      label: 'Success',
      value: p.success_rate === null ? '—' : p.success_rate,
      unit: p.success_rate === null ? undefined : '%',
      tone:
        p.success_rate === null
          ? 'default'
          : p.success_rate >= 95
            ? 'success'
            : p.success_rate >= 80
              ? 'warning'
              : 'destructive',
    },
    { label: 'p95', value: p.p95_ms, unit: 'ms' },
    {
      label: 'Failed',
      value: p.fail_24h,
      tone: p.fail_24h > 0 ? 'destructive' : 'default',
    },
  ];
}

function lastNoteMetrics(n: DashboardLastNote): KpiMetric[] {
  const total = n.done_24h + n.skipped_24h + n.failed_24h + n.dead_24h;
  const successRate = total > 0 ? Math.round((n.done_24h / total) * 100) : null;
  const fails = n.failed_24h + n.dead_24h;
  return [
    { label: 'Записано 24h', value: n.done_24h, tone: 'success' },
    { label: 'Пропущено 24h', value: n.skipped_24h },
    {
      label: 'Success',
      value: successRate === null ? '—' : successRate,
      unit: successRate === null ? undefined : '%',
      tone:
        successRate === null
          ? 'default'
          : successRate >= 95
            ? 'success'
            : successRate >= 80
              ? 'warning'
              : 'destructive',
    },
    { label: 'Ошибки', value: fails, tone: fails > 0 ? 'destructive' : 'default' },
  ];
}

function aiSummaryMetrics(a: DashboardAiSummary): KpiMetric[] {
  return [
    { label: 'Активных', value: `${a.configs_active}/${a.configs_total}` },
    { label: 'Runs 24h', value: a.runs_24h },
    {
      label: 'Лиды 24h',
      value: a.leads_processed_24h,
      tone: a.leads_processed_24h > 0 ? 'success' : 'default',
    },
    {
      label: 'Ошибки',
      value: a.fail_24h,
      tone: a.fail_24h > 0 ? 'destructive' : 'default',
    },
  ];
}

function gcalMetrics(g: DashboardGcal): KpiMetric[] {
  return [
    { label: 'Календарей', value: g.calendars_count },
    {
      label: 'События 24h',
      value: g.events_24h ?? '—',
      tone: g.events_24h && g.events_24h > 0 ? 'success' : 'default',
    },
    { label: 'События 7d', value: g.events_7d ?? '—' },
    {
      label: 'Статус',
      value: g.last_error ? 'Ошибка' : g.is_active ? 'OK' : 'Off',
      tone: g.last_error ? 'destructive' : g.is_active ? 'success' : 'default',
    },
  ];
}

// Человекочитаемые названия внешних интеграций (heartbeat-источников).
const EXTERNAL_SYNC_TITLES: Record<string, string> = {
  'happydent-kommo-cliniccards': 'Happydent · Kommo → ClinicCards',
};

function externalSyncMetrics(s: DashboardExternalSync): KpiMetric[] {
  return [
    { label: 'Прогонов 24h', value: s.runs_24h },
    {
      label: 'Ошибок 24h',
      value: s.fail_24h,
      tone: s.fail_24h > 0 ? 'destructive' : 'default',
    },
    { label: 'Последний', value: relativeTime(s.last_run_at) },
    {
      label: 'Статус',
      value: s.last_ok === false ? 'Ошибка' : s.last_ok === true ? 'OK' : '—',
      tone: s.last_ok === false ? 'destructive' : s.last_ok === true ? 'success' : 'default',
    },
  ];
}

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return 'только что';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} мин назад`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} ч назад`;
  const d = Math.round(h / 24);
  return `${d} дн назад`;
}

const LAST_NOTE_DEFAULT_TITLE = 'Last Note';
const AI_SUMMARY_DEFAULT_TITLE = 'AI Weekly Summary';

export function Dashboard(): JSX.Element {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardPipeline[]>('/admin/dashboard'),
    refetchInterval: 15_000,
  });

  const { data: extras } = useQuery({
    queryKey: ['dashboard-extras'],
    queryFn: () => api.get<DashboardExtras>('/admin/dashboard/extras'),
    refetchInterval: 15_000,
  });

  // Универсальная мутация переименования: card_type + card_id. Optimistic update
  // для обоих кешей (dashboard + dashboard-extras), при ошибке откатываем к снапшоту.
  const renameMutation = useMutation({
    mutationFn: async (vars: {
      cardType: DashboardCardType;
      cardId: string;
      title: string | null;
    }) =>
      api.patch<{ ok: boolean }>(
        `/admin/dashboard/cards/${vars.cardType}/${vars.cardId}`,
        { title: vars.title },
      ),
    onMutate: async (vars) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['dashboard'] }),
        queryClient.cancelQueries({ queryKey: ['dashboard-extras'] }),
      ]);
      const prevPipelines = queryClient.getQueryData<DashboardPipeline[]>(['dashboard']);
      const prevExtras = queryClient.getQueryData<DashboardExtras>(['dashboard-extras']);

      if (vars.cardType === 'pipeline' && prevPipelines) {
        queryClient.setQueryData<DashboardPipeline[]>(['dashboard'], (old) =>
          old?.map((p) =>
            p.id === vars.cardId
              ? {
                  ...p,
                  custom_title: vars.title,
                  display_name: vars.title ?? p.default_title,
                }
              : p,
          ),
        );
      }
      if (vars.cardType === 'last_note' && prevExtras?.last_note) {
        queryClient.setQueryData<DashboardExtras>(['dashboard-extras'], (old) =>
          old
            ? { ...old, last_note: { ...old.last_note!, custom_title: vars.title } }
            : old,
        );
      }
      if (vars.cardType === 'ai_summary' && prevExtras?.ai_summary) {
        queryClient.setQueryData<DashboardExtras>(['dashboard-extras'], (old) =>
          old
            ? { ...old, ai_summary: { ...old.ai_summary!, custom_title: vars.title } }
            : old,
        );
      }
      return { prevPipelines, prevExtras };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prevPipelines) queryClient.setQueryData(['dashboard'], ctx.prevPipelines);
      if (ctx?.prevExtras) queryClient.setQueryData(['dashboard-extras'], ctx.prevExtras);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-extras'] });
    },
  });

  const rename = (cardType: DashboardCardType, cardId: string) =>
    (title: string | null): void => {
      renameMutation.mutate({ cardType, cardId, title });
    };

  const lastNote = extras?.last_note ?? null;
  const aiSummary = extras?.ai_summary ?? null;
  const gcalList = extras?.gcal ?? [];
  const externalSyncList = extras?.external_sync ?? [];
  const hasExtras =
    lastNote !== null ||
    aiSummary !== null ||
    gcalList.length > 0 ||
    externalSyncList.length > 0;

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Состояние всех подключений за последние 24 часа
          </p>
        </div>
        <Button asChild>
          <Link to="/pipelines">
            <Workflow className="mr-2 h-4 w-4" />
            Управление pipelines
          </Link>
        </Button>
      </header>

      {isLoading && <CardGridSkeleton count={3} />}

      {error && (
        <Card>
          <CardContent className="pt-6 text-sm text-destructive">
            Ошибка загрузки: {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {data && data.length === 0 && !hasExtras && (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
            <Workflow className="h-10 w-10 text-muted-foreground" />
            <div>
              <h3 className="font-semibold">Подключений пока нет</h3>
              <p className="text-sm text-muted-foreground">
                Создайте первое подключение Kommo → Google Sheets.
              </p>
            </div>
            <Button asChild>
              <Link to="/pipelines">Создать подключение</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {(data && data.length > 0) || hasExtras ? (
        <motion.div
          className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
        >
          {data?.map((p) => (
            <motion.div
              key={p.id}
              variants={{
                hidden: { opacity: 0, y: 12 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } },
              }}
            >
              <PipelineKpiCard
                title={p.display_name}
                defaultTitle={p.default_title}
                isCustomTitle={p.custom_title !== null}
                onTitleChange={rename('pipeline', p.id)}
                isSavingTitle={renameMutation.isPending}
                status={p.status}
                metrics={metricsFor(p)}
                errorMessage={p.last_error}
                href={`/pipelines/${p.id}`}
              />
            </motion.div>
          ))}

          {lastNote && (
            <motion.div
              key="last-note"
              variants={{
                hidden: { opacity: 0, y: 12 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } },
              }}
            >
              <PipelineKpiCard
                title={lastNote.custom_title ?? LAST_NOTE_DEFAULT_TITLE}
                defaultTitle={LAST_NOTE_DEFAULT_TITLE}
                isCustomTitle={lastNote.custom_title !== null}
                onTitleChange={rename('last_note', 'singleton')}
                isSavingTitle={renameMutation.isPending}
                status="active"
                metrics={lastNoteMetrics(lastNote)}
                errorMessage={lastNote.last_error}
                href="/last-note"
                linkLabel="Открыть Last Note"
              />
            </motion.div>
          )}

          {aiSummary && (
            <motion.div
              key="ai-summary"
              variants={{
                hidden: { opacity: 0, y: 12 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } },
              }}
            >
              <PipelineKpiCard
                title={aiSummary.custom_title ?? AI_SUMMARY_DEFAULT_TITLE}
                defaultTitle={AI_SUMMARY_DEFAULT_TITLE}
                isCustomTitle={aiSummary.custom_title !== null}
                onTitleChange={rename('ai_summary', 'singleton')}
                isSavingTitle={renameMutation.isPending}
                status={aiSummary.configs_active > 0 ? 'active' : 'paused'}
                metrics={aiSummaryMetrics(aiSummary)}
                errorMessage={aiSummary.last_error}
                href="/ai-summary"
                linkLabel="Открыть AI Summary"
              />
            </motion.div>
          )}

          {gcalList.map((g) => (
            <motion.div
              key={`gcal-${g.integration_id}`}
              variants={{
                hidden: { opacity: 0, y: 12 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } },
              }}
            >
              <PipelineKpiCard
                title={`Calendar · ${g.account_name}`}
                status={g.is_active ? 'active' : 'paused'}
                metrics={gcalMetrics(g)}
                errorMessage={
                  g.last_error
                    ? `${g.last_error} · last sync ${relativeTime(g.last_fetched_at)}`
                    : null
                }
                href="/gcal"
                linkLabel="Открыть Google Calendar"
              />
            </motion.div>
          ))}

          {externalSyncList.map((s) => (
            <motion.div
              key={`ext-${s.source}`}
              variants={{
                hidden: { opacity: 0, y: 12 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } },
              }}
            >
              <PipelineKpiCard
                title={EXTERNAL_SYNC_TITLES[s.source] ?? s.source}
                status={s.last_ok === false ? 'paused' : 'active'}
                metrics={externalSyncMetrics(s)}
                errorMessage={
                  s.last_ok === false
                    ? `Последний прогон с ошибкой · ${relativeTime(s.last_run_at)}`
                    : null
                }
              />
            </motion.div>
          ))}
        </motion.div>
      ) : null}
    </div>
  );
}
